import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import moment from 'moment';
import { config } from 'chaoskit/src/helpers/config';

import { Highlight, connectRefinementList } from 'react-instantsearch-dom';

import { Icon, Link } from '../../components';
import useAuthor from '../../hooks/useAuthor';

function findMicrosite(filePath) {
  if (/(src\/chaos-monkey)/.test(filePath)) {
    return 'Chaos Monkey';
  }

  if (/(src\/chaos-engineering)/.test(filePath)) {
    return 'Chaos Engineering';
  }

  return null;
}

function getResourceInfo(hit) {
  const isObject = typeof hit === 'object';
  const switchTest = isObject ? hit.articleLabel : hit;

  switch (switchTest) {
    case 'Blog Posts':
      return {
        icon: 'resources__blog',
        meta: isObject && hit.author,
        metaSub: isObject && hit.date && moment.unix(hit.date).format('LL'),
      };
    case 'Books':
      return {
        icon: 'resources__book',
      };
    case 'Case Studies':
      return {
        icon: 'resources__case-study',
        metaLogo: isObject && hit.logo,
      };
    case 'Docs':
      return {
        icon: 'resources__doc',
        meta: isObject && hit.section,
      };
    case 'Events':
      return {
        icon: 'resources__event',
      };
    case 'Microsites':
      return {
        icon: 'resources__microsite',
        meta: isObject && findMicrosite(hit.fileAbsolutePath),
      };
    case 'Podcasts':
      return {
        icon: 'resources__podcast',
      };
    case 'Talks':
      return {
        icon: 'resources__talk',
      };
    case 'Tutorials':
      return {
        icon: 'resources__tutorial',
        meta: isObject && hit.author,
        metaSub:
          isObject &&
          hit.dateModified &&
          moment.unix(hit.dateModified).format('LL'),
      };
    case 'Webinars':
      return {
        icon: 'resources__webinar',
        meta:
          isObject && !hit.onDemand && hit.date
            ? moment.unix(hit.date).format('LL')
            : 'On-Demand',
        metaSub: isObject && !hit.onDemand && hit.time ? hit.time : null,
      };
    case 'Whitepapers':
      return {
        icon: 'resources__whitepaper',
      };
    default:
      return {
        icon: 'resources__default',
      };
  }
}
export const Hit = ({ hit }) => {
  const author = useAuthor(hit.author);

  return (
    <Link
      to={`${hit.slug}${hit.contentId ? `#${hit.contentId}` : ''}`}
      className="resources__hit"
    >
      <div className="resources__hit-top">
        <div className="u-textHeading u-textNormal u-textSpace u-textXsmall u-textMuted u-textUppercase">
          {hit.articleLabel}
        </div>
        <h3 className="u-mt--small u-textLarge">
          <Highlight tagName="mark" attribute="title" hit={hit} />
        </h3>
      </div>
      <div className="resources__hit-meta">
        <div className="resources__hit-avatar">
          {hit.author ? (
            <img src={author.frontmatter.photo} alt={author.frontmatter.name} />
          ) : (
            <Icon icon={getResourceInfo(hit).icon} />
          )}
        </div>
        <div className="resources__hit-metaContent">
          {getResourceInfo(hit).meta && (
            <div className="u-textDefault u-textMedium u-textHeading">
              {getResourceInfo(hit).meta}
            </div>
          )}
          {getResourceInfo(hit).metaLogo && (
            <img
              src={getResourceInfo(hit).metaLogo}
              alt=""
              className="resources__hit-metaLogo"
            />
          )}
          {getResourceInfo(hit).metaSub && (
            <div className="u-textMuted u-textSmall">
              {getResourceInfo(hit).metaSub}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
};

Hit.propTypes = {
  hit: PropTypes.object,
};

const TypesRefinementList = ({ items, refine, createURL }) =>
  items.length > 0 ? (
    <ul className="resources__topicsGrid">
      {items.map(item => (
        <li key={item.label}>
          <a
            className={cx('resources__refineLink--type', {
              [config.classes.active]: item.isRefined,
            })}
            href={createURL(item.value)}
            onClick={event => {
              event.preventDefault();
              refine(item.value);
            }}
            title={`${item.isRefined ? 'Remove' : 'Add'} ${item.label}`}
          >
            <Icon
              size="large"
              className="u-mr--small"
              icon={getResourceInfo(item.label).icon}
            />{' '}
            {item.label}
            {item.isRefined && (
              <Icon
                className="resources__refineLink-close"
                icon="close"
                size="small"
              />
            )}
          </a>
        </li>
      ))}
    </ul>
  ) : (
    <p className="u-textSmall u-textMuted">No types found</p>
  );

TypesRefinementList.propTypes = {
  items: PropTypes.array,
  refine: PropTypes.func,
  createURL: PropTypes.func,
};

export const CustomTypesRefinementList = connectRefinementList(
  TypesRefinementList
);

const TopicsRefinementList = ({ items, refine, createURL }) =>
  items.length > 0 ? (
    <ul className="resources__topicsGrid">
      {items.map(item => (
        <li key={item.label}>
          <a
            className={cx('resources__refineLink--topic', {
              [config.classes.active]: item.isRefined,
            })}
            href={createURL(item.value)}
            onClick={event => {
              event.preventDefault();
              refine(item.value);
            }}
            title={`${item.isRefined ? 'Remove' : 'Add'} ${item.label}`}
          >
            {item.label}
            {item.isRefined && (
              <Icon
                className="resources__refineLink-close"
                icon="close"
                size="small"
              />
            )}
          </a>
        </li>
      ))}
    </ul>
  ) : (
    <p className="u-textSmall u-textMuted">No topics found</p>
  );

TopicsRefinementList.propTypes = {
  items: PropTypes.array,
  refine: PropTypes.func,
  createURL: PropTypes.func,
};

export const CustomTopicsRefinementList = connectRefinementList(
  TopicsRefinementList
);
